import React, { useContext, useEffect, useState } from "react";
import { ThreeRow } from "../../core/layouts";
import variables from "../../themes/base/styles/variables";
import {
  Title,
  Text,
  Space,
  Checkbox,
  Button,
  Spin,
} from "react_reusable_components";
import { Avatar } from "antd";
import { AuthContext } from "../../globalContext/auth/authProvider";
import HeadingContainer from "../../containers/HeadingContainer/HeadingContainer";
import Style from "./MyAcountViewContainer.module.scss";
import useResponsive from "core/hooks/useResponsive";
import MyAccountModal from "../ResetPasswordModal";
import { useIntl } from "react-intl";
import {
  decryptAuthData,
  getLocalizedMessage,
} from "../../UtilityFunctions/utils";
import MyAccountService from "../../services/my-account-services";
import { setUser } from "../../globalContext/auth/authActions";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import MediaService from "../../services/media-service";
import { StorageService } from "../../services";

const MyAccountViewContainerStyle = {
  avatar: {
    background: variables.avatarBG,
    height: variables.avatarLg,
    width: variables.avatarLg,
    display: variables.dFlex,
    alignItems: variables.alignCenter,
    margin: `0 ${variables.marginAuto}`,
    fontSize: variables.fontSizeXxlarge,
    fontWeight: variables.fontWeightNormal,
  },
  title: {
    fontSize: variables.fontSizeXxlarge,
    fontWeight: variables.fontWeightBold,
    marginTop: variables.marginMedium,
    textAlign: variables.alignCenter,
    marginBottom: variables.marginSmall,
    lineHeight: variables.lineHeightXxlarge,
    textTransform: "capitalize",
  },
  email: {
    fontSize: variables.fontSizeNormal,
    fontWeight: variables.fontWeightNormal,
    textAlign: variables.alignCenter,
    marginBottom: 0,
    lineHeight: variables.lineHeightSmall,
  },
  title2: {
    fontSize: variables.fontSizeXlarge,
    fontWeight: variables.fontWeightBold,
  },
  checkboxStyle: {
    fontSize: variables.fontSizeNormal,
    fontWeight: variables.fontWeightNormal,
    lineHeight: variables.lineHeightNormal,
    marginTop: variables.marginMedium2,
    gap: variables.gapXS2,
  },
  style: {
    padding: `0 ${variables.paddingMedium}`,
  },
  topSectionStyle: {
    padding: `${variables.paddingMediumLarge} 0`,
    borderBottom: `1px solid ${variables.grey4}`,
    display: variables.dFlex,
    justifyContent: variables.alignCenter,
  },
  bottomSectionStyle: { padding: `${variables.paddingMedium} 0` },
  middleSectionStyle: {
    padding: `${variables.paddingMedium} 0`,
    borderBottom: `1px solid ${variables.grey4}`,
  },
};

export default function MyAccountViewContainer() {
  const responsive = useResponsive();
  const intl = useIntl();
  const [open, setOpen] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [authState, authDispatch] = useContext(AuthContext);
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const [imageDataUrl, setImageDataUrl] = useState("");
  const {
    name: userName,
    email,
    isEmailNotification,
    isSmsNotification,
    isWebPortalNotification,
    imageId = "",
  } = authState?.data || {};
  const [settings, setSettings] = useState({
    is_email_notification: isEmailNotification,
    is_sms_notification: isSmsNotification,
    is_web_portal_notification: isWebPortalNotification,
  });

  const onChange = (e) => {
    setIsLoading(true);
    MyAccountService.changeUserSettings({
      body: { ...settings, [e.target.name]: e.target.checked },
      intl,
    })
      .then((res) => {
        setSettings((prev) => ({
          ...prev,
          [e.target.name]: e.target.checked,
        }));
        StorageService.set("token", {
          token: res?.headers?.authorization,
        });
        const userData = decryptAuthData(res?.headers?.authorization);
        authDispatch(setUser(userData));
      })
      .catch(() => {}) //NOSONAR
      .finally(() => {
        setIsLoading(false);
      });
  };

  const settingsCheckboxes = [
    {
      style: {
        ...MyAccountViewContainerStyle.checkboxStyle,
        marginTop: variables.marginSmallest,
      },
      text: getLocalizedMessage(intl, "account.receiveOverEmail"),
      name: "is_email_notification",
      onChange,
    },
    {
      style: MyAccountViewContainerStyle.checkboxStyle,
      text: getLocalizedMessage(intl, "account.receiveOverSms"),
      name: "is_sms_notification",
      onChange,
    },
    // {
    //   style: MyAccountViewContainerStyle.checkboxStyle,
    //   text: getLocalizedMessage(intl, "account.webPortalNotifications"),
    //   name: "is_web_portal_notification",
    //   onChange,
    // },
  ];

  useEffect(() => {
    if (imageId) {
      MediaService?.getMediaData({
        loaderDispatch,
        mediaId: imageId,
        intl,
      }).then((res) => {
        setImageDataUrl(URL.createObjectURL(res?.data));
      });
    }
  }, [imageId]);

  return (
    <ThreeRow
      style={MyAccountViewContainerStyle.style}
      topSectionStyle={MyAccountViewContainerStyle.topSectionStyle}
      topSection={
        <Space size={0} direction="vertical">
          <Avatar
            style={{
              ...MyAccountViewContainerStyle.avatar,
              ...(imageDataUrl?.length > 0 && { background: "none" }),
            }}
            src={imageDataUrl}
          >
            {userName?.[0]?.toUpperCase()}
            {userName?.split?.(" ")?.[1]?.[0]?.toUpperCase()}
          </Avatar>
          <Title style={MyAccountViewContainerStyle.title}>{userName}</Title>
          <Text style={MyAccountViewContainerStyle.email}>{email}</Text>
        </Space>
      }
      middleSectionStyle={MyAccountViewContainerStyle.middleSectionStyle}
      middleSection={
        <Space size={0} direction="vertical">
          <Title style={MyAccountViewContainerStyle.title2}>
            {getLocalizedMessage(intl, "account.notificationSettings")}
          </Title>
          {settingsCheckboxes.map(
            ({ style, text, name, onChange: handleChange }) => (
              <Checkbox
                key={text}
                style={
                  style ? style : MyAccountViewContainerStyle.checkboxStyle
                }
                text={text}
                checked={settings[name]}
                name={name}
                onChange={handleChange}
              />
            )
          )}
        </Space>
      }
      bottomSectionStyle={MyAccountViewContainerStyle.bottomSectionStyle}
      bottomSection={
        <>
          <Space direction={"vertical"}>
            <HeadingContainer
              headingClassName={Style["heading-change-pass"]}
              heading={getLocalizedMessage(intl, "account.changePassword")}
              description={getLocalizedMessage(
                intl,
                "account.changePasswordDescription"
              )}
              descriptionClassName={Style["description-class"]}
            />
            <Button
              className={`${Style["change-password-btn"]} ${
                !responsive.isMd ? Style["full-width"] : ""
              }`}
              onClick={() => setOpen(true)}
              ghost
              icon={null}
            >
              {getLocalizedMessage(intl, "account.changePassword")}
            </Button>
          </Space>
          <MyAccountModal open={open} onClose={() => setOpen(false)} />
          {isLoading && (
            // need to remove when loader is moved to app level
            <Spin size={"large"} type={"fullscreen"} backgroundBlur={0.5} />
          )}
        </>
      }
    />
  );
}
